import { Descriptions, Space, Tag, Tooltip } from 'antd'

import { FormattedDate } from '@/components/common/FormattedDate'

import {
  formatHours,
  formatRunway,
  formatUsd,
  formatUsdHourly,
} from '../format'
import type { GpuBalance } from '../hooks/useGpuAdmin'

const DATE_FMT = 'MMM d, HH:mm'

function isStale(balance: GpuBalance) {
  return Boolean(
    balance.validUntil && new Date(balance.validUntil) < new Date(),
  )
}

function BalanceTags({ balance }: { balance: GpuBalance }) {
  return (
    <>
      {isStale(balance) && <Tag color="orange">Stale</Tag>}
      {balance.salesFloorBlocksSales && (
        <Tooltip title="Offers are held back by the balance floor">
          <Tag color="red">Below floor</Tag>
        </Tooltip>
      )}
    </>
  )
}

export function BalanceSummary({ balance }: { balance: GpuBalance }) {
  if (!balance.measurable) return <Tag>Not measurable</Tag>
  if (balance.totalUsd === null || balance.totalUsd === undefined) {
    return <Tag>No reading yet</Tag>
  }
  return (
    <Space size={4} wrap>
      {formatUsd(balance.totalUsd)}
      <span>{`· ${formatRunway(balance.totalRunwayHours, balance.totalUsd)} left`}</span>
      <BalanceTags balance={balance} />
    </Space>
  )
}

export function BalanceDetails({ balance }: { balance: GpuBalance }) {
  if (!balance.measurable) {
    return <Tag>Provider balance is not measurable</Tag>
  }
  return (
    <Descriptions size="small" column={{ xs: 1, md: 2 }}>
      <Descriptions.Item label="Total">
        {formatUsd(balance.totalUsd)}
      </Descriptions.Item>
      <Descriptions.Item label="Available">
        {formatUsd(balance.availableUsd)}
      </Descriptions.Item>
      <Descriptions.Item label="Burn">
        {formatUsdHourly(balance.usdPerHour)}
      </Descriptions.Item>
      <Descriptions.Item label="Runway (total / available)">
        {`${formatRunway(balance.totalRunwayHours, balance.totalUsd)} / ${formatRunway(balance.availableRunwayHours, balance.availableUsd)}`}
      </Descriptions.Item>
      <Descriptions.Item label="Sales floor">
        <Space size={4}>
          {formatHours(balance.salesFloorHours)}
          <BalanceTags balance={balance} />
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="Observed">
        <FormattedDate date={balance.observedAt} fmt={DATE_FMT} />
        {' · valid until '}
        <FormattedDate date={balance.validUntil} fmt={DATE_FMT} />
      </Descriptions.Item>
    </Descriptions>
  )
}
